import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  AfterViewInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormBuilder,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  WrxlRulesService,
  WxrlOperator,
  WxrlCondition,
} from '../../services/wxrl-rules.service';
import { normalizeSnFlag, normalizeVisibleFlag } from '../../utils/wxrl-sn-flag';

/**
 * Componente drawer para editar/crear condiciones de reglas WXRL.
 *
 * Este componente maneja el formulario de edición de condiciones con lógica dinámica
 * basada en el operador seleccionado. Los campos se muestran/ocultan según los flags
 * del operador (enableV1, enableV2, etc.).
 *
 * **Campos dinámicos:**
 *
 * 1. **ID Elemento (saveValue):**
 *    - Se muestra cuando enableV1 === 1
 *    - Puede ser dropdown simple o multiselect según multiSelectV1
 *    - Las opciones se cargan ejecutando QRY1 en el backend
 *    - Relacionado con el checkbox "Heredar":
 *      * Si "Heredar" está marcado → SaveValue = '-1' (hereda del disparador)
 *      * Si "Heredar" está desmarcado → SaveValue = valor seleccionado
 *
 * 2. **Valor de Entrada (entryValue):**
 *    - Se muestra cuando enableV2 === 1
 *    - Tipo de control determinado por QRY2:
 *      * Si QRY2 tiene consulta SQL → Dropdown con opciones del backend
 *      * Si QRY2 está vacío → Textbox para texto libre
 *
 * **Flujo de carga:**
 * 1. Usuario selecciona "Actúa Sobre" (condId) → Se cargan operadores disponibles
 * 2. Usuario selecciona "Operador" → Se cargan opciones de ID Elemento (QRY1) y Valor de Entrada (QRY2)
 * 3. Si QRY2 devuelve opciones → enableV2Dropdown = true → Dropdown o p-multiSelect si multiSelectV2
 * 4. Si QRY2 está vacío → enableV2Dropdown = false → Textbox (no obligatorio)
 *
 * @component
 * @selector afoone-wxrl-condition-drawer
 */
@Component({
  selector: 'afoone-wxrl-condition-drawer',
  templateUrl: './wxrl-condition-drawer.component.html',
  styleUrls: ['./wxrl-condition-drawer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WxrlConditionDrawerComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() visible: boolean = false;
  @Input() condition: WxrlCondition | null = null;
  @Input() initialCondRule: string | null = null; // Para establecer el condRule inicial en modo nueva regla
  @Input() isEditMode: boolean = false;
  @Input() actuaSobreOptions: any[] = [];
  @Input() perfilOptions: any[] = [];
  @Input() tipoResOptions: any[] = [];
  @Input() aislamientoOptions: any[] = [];
  @Input() isNewRule: boolean = false;
  @Input() labNr?: number;
  /** Tipo de laboratorio; aislamiento solo aplica en LABM (mismo criterio que wxrl-action-drawer). */
  @Input() labType: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  public conditionForm: FormGroup;
  public dynamicOperatorOptions: WxrlOperator[] = [];
  public elementoOptions: any[] = [];
  public entryValueOptions: any[] = [];
  public selectedOperatorInfo: string = '';
  public readonly visibleOptions = [
    { label: 'Visible', value: 'S' },
    { label: 'No visible', value: 'N' },
    { label: 'No aplica', value: '' },
  ];

  /**
   * Control de visibilidad de campos dinámicos basados en el operador seleccionado.
   * Estos flags se obtienen de la estructura del operador (WxrlOperator) que viene del backend.
   *
   * @property {boolean} enableV1 - Indica si se debe mostrar el grupo de campos V1:
   *   - ID Elemento (saveValue)
   *   - Checkbox "Heredar"
   *   - Perfil (grupNr)
   *   Se activa cuando el operador tiene enableV1 === 1
   *
   * @property {boolean} enableV1Elemento - Indica si se debe mostrar el dropdown/multiselect
   *   para "ID Elemento". Se activa cuando hay opciones disponibles después de ejecutar QRY1.
   *   Si es false, solo se muestra el checkbox "Heredar" sin dropdown.
   *
   * @property {boolean} multiSelectV1 - Indica si el campo "ID Elemento" debe ser un
   *   multiselect (true) o un dropdown simple (false). Cuando es multiselect, el saveValue
   *   se guarda como string separado por comas (ej: "100170,100180").
   *
   * @property {boolean} enableV2 - Indica si se debe mostrar el campo "Valor de Entrada" (entryValue).
   *   Se activa cuando el operador tiene enableV2 === 1.
   *
   * @property {boolean} enableV2Dropdown - Determina el tipo de control para "Valor de Entrada":
   *   - true: Se muestra un dropdown (p-dropdown) porque QRY2 contiene una consulta SQL
   *           que se ejecuta en el backend y devuelve opciones.
   *   - false: Se muestra un textbox (input text) porque QRY2 está vacío, permitiendo
   *            texto libre.
   *   Esta propiedad se establece en loadEntryValueOptions() basándose en si el backend
   *   devuelve opciones o un array vacío/null.
   *
   * @property {boolean} multiSelectV2 - Indica si el campo "Valor de Entrada" debe ser un
   *   multiselect cuando enableV2Dropdown es true. Actualmente no se usa en el HTML.
   */
  public enableV1: boolean = false;
  public enableV1Elemento: boolean = false;
  public multiSelectV1: boolean = false;
  public enableV2: boolean = false;
  public enableV2Dropdown: boolean = false;
  public multiSelectV2: boolean = false;

  // Datos del operador seleccionado para queries dinámicas
  private currentOperatorData: any = null;
  private isLoadingOperatorOptions: boolean = false;

  /** Panel RULESCONDITIONC.Info (#551), patrón wxrl-action-drawer */
  public showOperatorInfo = false;
  private operatorInfoTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private _fb: FormBuilder,
    private _cdr: ChangeDetectorRef,
    private _translate: TranslateService,
    private _wxrlService: WrxlRulesService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Suscripción a cambios de condId para recargar operadores dinámicos
    this.conditionForm.get('condId')?.valueChanges.subscribe((condId) => {
      if (this.visible && condId) {
        this.loadDynamicOperators(condId).then(() => {
          this.conditionForm.patchValue(
            {
              rOperator: '',
              labelOperator: '',
              catalogROperator: '',
              saveValue: '',
              entryValue: '',
              heredar: false,
            },
            { emitEvent: false }
          );
          this.resetDynamicFields(true);
        });
      }
    });

    // Validación dinámica: saveValue es requerido si enableV1Elemento es true y heredar es false
    // También limpiar el valor del dropdown cuando "Heredar" se marca
    this.conditionForm.get('heredar')?.valueChanges.subscribe((heredarValue) => {
      if (heredarValue) {
        // Si se marca "Heredar", limpiar el valor del dropdown
        this.conditionForm.patchValue({ saveValue: '' }, { emitEvent: false });
      }
      this.updateSaveValueValidation();
      this._cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.closeOperatorInfo();
  }

  ngAfterViewInit(): void {
    // Envuelve TODA la detección de cambios en un setTimeout
    setTimeout(() => {
      // Si condition está disponible y estamos en modo nuevo, establecer el condRule
      if (this.visible && this.condition && !this.isEditMode && !this.conditionForm.get('condRule')?.value) {
        this.conditionForm.patchValue({
          condRule: this.condition.condRule || '',
          posC: this.condition.posC || 0
        });
      }
      this._cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labType'] && this.labType !== 'LABM') {
      this.conditionForm.patchValue({ isolate: '' }, { emitEvent: false });
      this._cdr.markForCheck();
    }

    if (changes['visible'] && this.visible) {
      if (this.condition) {
        // Modo edición: cargar datos de la condición existente
        this.loadConditionData();
      } else {
        // Modo nuevo: resetear el formulario
        this.resetForm();
        // Establecer condRule desde initialCondRule si está disponible (modo nueva regla)
        if (this.initialCondRule && !this.conditionForm.get('condRule')?.value) {
          this.conditionForm.patchValue({
            condRule: this.initialCondRule,
            posC: this.condition?.posC || 0
          });
          this._cdr.markForCheck();
        }
        // Usar setTimeout para esperar a que condition llegue si viene después
        setTimeout(() => {
          if (this.condition?.condRule && !this.conditionForm.get('condRule')?.value) {
            this.conditionForm.patchValue({
              condRule: this.condition.condRule,
              posC: this.condition.posC || 0
            });
            this._cdr.markForCheck();
          }
        }, 100);
      }
    } else if (changes['visible'] && !this.visible) {
      this.resetForm();
    }

    // Si condition cambia y tiene un valor y estamos en modo nuevo (no edición)
    // Esto maneja el caso cuando condition se establece después de que visible se vuelve true
    if (changes['condition'] && this.condition && this.visible && !this.isEditMode) {
      // Establecer el condRule generado automáticamente
      this.conditionForm.patchValue({
        condRule: this.condition.condRule || '',
        posC: this.condition.posC || 0
      });
      this._cdr.markForCheck();
    }

    // Si visible cambia a true y condition ya está disponible (puede pasar en cualquier orden)
    if (changes['visible'] && this.visible && this.condition && !this.isEditMode && !this.conditionForm.get('condRule')?.value) {
      // Establecer el condRule si aún no está en el formulario
      this.conditionForm.patchValue({
        condRule: this.condition.condRule || '',
        posC: this.condition.posC || 0
      });
      this._cdr.markForCheck();
    }

    // Verificación adicional: si visible es true y condition tiene valor pero no se ha establecido en el formulario
    // Esto cubre el caso cuando condition se establece en el mismo ciclo que visible cambia
    if (this.visible && this.condition && !this.isEditMode) {
      const currentCondRule = this.conditionForm.get('condRule')?.value;
      if (!currentCondRule && this.condition.condRule) {
        // Usar setTimeout para asegurar que el formulario esté listo
        setTimeout(() => {
          this.conditionForm.patchValue({
            condRule: this.condition!.condRule || '',
            posC: this.condition!.posC || 0
          });
          this._cdr.markForCheck();
        }, 100);
      }
    }
  }

  /**
   * Clave estable por fila de catálogo (IdOperator + PosR). Evita #546 cuando PosR se repite.
   */
  private withOperatorKeys(operators: WxrlOperator[]): WxrlOperator[] {
    return (operators || []).map((op) => ({
      ...op,
      operatorKey: `${String(op.idOperator ?? '')}|${Number(op.posR)}`,
    }));
  }

  /**
   * Texto de ayuda del operador (RULESCATALOGCONDITIONDEF.Info → API `info`).
   * Normaliza por si el driver SQL devuelve `Info` / `iNFO` tras cleanup parcial.
   */
  private readOperatorInfo(
    op: WxrlOperator | Record<string, unknown> | null | undefined
  ): string {
    if (!op || typeof op !== 'object') {
      return '';
    }
    const r = op as Record<string, unknown>;
    const v = r['info'] ?? r['Info'] ?? r['iNFO'];
    if (v == null) {
      return '';
    }
    return String(v).trim();
  }

  private findOperatorBySelection(sel: unknown): WxrlOperator | undefined {
    if (sel === '' || sel === null || sel === undefined) {
      return undefined;
    }
    const ops = this.dynamicOperatorOptions;
    if (typeof sel === 'string' && sel.includes('|')) {
      const byKey = ops.find((o) => o.operatorKey === sel);
      if (byKey) {
        return byKey;
      }
    }
    const n = Number(sel);
    if (!Number.isNaN(n)) {
      const match = ops.filter((o) => Number(o.posR) === n);
      if (match.length === 1) {
        return match[0];
      }
    }
    return undefined;
  }

  /** Fallback cuando operatorKey / findOperatorBySelection no resuelven (#545 / #546). */
  private pickOperatorByPosRAndLabel(
    posR: number,
    labelOperator?: string
  ): WxrlOperator | undefined {
    const candidates = this.dynamicOperatorOptions.filter(
      (o) => Number(o.posR) === posR
    );
    if (candidates.length === 0) {
      return undefined;
    }
    if (candidates.length === 1) {
      return candidates[0];
    }
    const label = (labelOperator || '').trim();
    if (label) {
      const byLabel = candidates.find(
        (o) => (o.labelOperator || '').trim() === label
      );
      if (byLabel) {
        return byLabel;
      }
    }
    return candidates[0];
  }

  private hasNonEmptyEntryValue(v: unknown): boolean {
    if (v === undefined || v === null) {
      return false;
    }
    if (typeof v === 'number') {
      return !Number.isNaN(v);
    }
    if (typeof v === 'boolean') {
      return true;
    }
    return String(v).trim() !== '';
  }

  /** Combo/multiselect «Valor de entrada»: obligatorio si está visible (#544 §8). */
  private validateEntryValueComboRequired(
    control: AbstractControl
  ): ValidationErrors | null {
    const v = control.value;
    if (v == null) {
      return { required: true };
    }
    if (Array.isArray(v)) {
      return v.length === 0 ? { required: true } : null;
    }
    return this.hasNonEmptyEntryValue(v) ? null : { required: true };
  }

  /** Resuelve la clave del dropdown al cargar una condición existente (API puede traer idOperator). */
  private resolveOperatorKeyForCondition(cv: WxrlCondition): string {
    const posNum = Number(cv.rOperator);
    const ops = this.dynamicOperatorOptions;
    if (!ops.length || Number.isNaN(posNum)) {
      return '';
    }
    const idOp =
      cv.idOperator != null && cv.idOperator !== ''
        ? String(cv.idOperator)
        : '';
    if (idOp) {
      const key = `${idOp}|${posNum}`;
      if (ops.some((o) => o.operatorKey === key)) {
        return key;
      }
    }
    const label = (cv.labelOperator || '').trim();
    const candidates = ops.filter((o) => Number(o.posR) === posNum);
    if (candidates.length === 1) {
      return candidates[0].operatorKey ?? '';
    }
    if (candidates.length > 1 && label) {
      const byLabel = candidates.find(
        (o) => (o.labelOperator || '').trim() === label
      );
      if (byLabel?.operatorKey) {
        return byLabel.operatorKey;
      }
    }
    return candidates[0]?.operatorKey ?? '';
  }

  private initializeForm(): void {
    this.conditionForm = this._fb.group({
      condRule: ['', Validators.required],
      posC: [0],
      kommentar: ['', [Validators.maxLength(255)]],
      condId: ['', Validators.required],
      saveValue: [''],
      heredar: [false],
      grupNr: [''],
      rOperator: ['', Validators.required],
      labelOperator: [''],
      catalogROperator: [''],
      entryValue: [''],
      negCond: ['N'],
      exitCond: ['N'],
      visible: ['S'],
      isolate: [''],
      tipoRes: [''],
    });
  }

  /**
   * Carga los datos de una condición existente para editar.
   *
   * Este método se ejecuta cuando se abre el drawer en modo edición. Realiza las siguientes acciones:
   *
   * 1. Carga los operadores dinámicos según el condId seleccionado
   * 2. Encuentra el operador seleccionado (rOperator) y establece los flags dinámicos:
   *    - enableV1, enableV2, multiSelectV1, multiSelectV2
   * 3. Carga las opciones para "ID Elemento" si enableV1 es true (ejecuta QRY1 en backend)
   * 4. Carga las opciones para "Valor de Entrada" si enableV2 es true (ejecuta QRY2 en backend)
   * 5. Establece los valores del formulario, incluyendo:
   *    - saveValue: Si es '-1' → checkbox "Heredar" marcado y dropdown vacío
   *                 Si no es '-1' → checkbox desmarcado y dropdown con el valor seleccionado
   *    - entryValue: El valor guardado (puede ser de dropdown o textbox según QRY2)
   *
   * @private
   * @async
   * @returns {Promise<void>}
   */
  private async loadConditionData(): Promise<void> {
    if (!this.condition) return;

    const conditionValue = this.condition;
    console.log('[ConditionDrawer] Loading condition data:', conditionValue);
    this.conditionForm.patchValue(
      { condId: conditionValue.condId || '' },
      { emitEvent: false }
    );

    // Cargar operadores dinámicos según condId
    if (conditionValue.condId) {
      await this.loadDynamicOperators(conditionValue.condId);
      console.log('[ConditionDrawer] Dynamic operators loaded:', this.dynamicOperatorOptions);
    }

    // Preparar todos los valores antes de establecerlos en el formulario
    let parsedSaveValue =
      conditionValue.saveValue === '-1' ? '' : conditionValue.saveValue;
    console.log('[ConditionDrawer] Initial parsedSaveValue:', parsedSaveValue);

    const operatorKeyResolved =
      this.resolveOperatorKeyForCondition(conditionValue);

    // Si hay operador seleccionado, cargar sus datos dinámicos primero
    if (conditionValue.rOperator != null && conditionValue.rOperator !== '') {
      const posR =
        typeof conditionValue.rOperator === 'number'
          ? conditionValue.rOperator
          : Number(conditionValue.rOperator);
      console.log('[ConditionDrawer] Looking for operator with posR:', posR);
      // #545: PosR puede ser 0 — no usar `posR &&` (0 es falsy)
      if (Number.isFinite(posR) && this.dynamicOperatorOptions.length > 0) {
        this.currentOperatorData =
          this.findOperatorBySelection(operatorKeyResolved) ||
          this.findOperatorBySelection(conditionValue.rOperator);
        if (!this.currentOperatorData) {
          this.currentOperatorData = this.pickOperatorByPosRAndLabel(
            posR,
            conditionValue.labelOperator
          );
        }
        console.log('[ConditionDrawer] Found operator data:', this.currentOperatorData);
        if (this.currentOperatorData) {
          this.enableV1 = this.currentOperatorData.enableV1 === 1;
          this.multiSelectV1 = this.currentOperatorData.multiSelectV1 === 1;
          this.enableV2 = this.currentOperatorData.enableV2 === 1;
          this.multiSelectV2 = this.currentOperatorData.multiSelectV2 === 1;
          this.selectedOperatorInfo = this.currentOperatorData.info || '';
          console.log('[ConditionDrawer] Operator flags - enableV1:', this.enableV1, 'multiSelectV1:', this.multiSelectV1);

          // Parsear saveValue si es multiselect
          parsedSaveValue = this.parseSaveValueForEdit(
            conditionValue.saveValue
          );
          console.log('[ConditionDrawer] Parsed saveValue after parseSaveValueForEdit:', parsedSaveValue, 'type:', typeof parsedSaveValue);

          // Cargar datos dinámicos usando idOperator
          if (this.enableV1 && this.currentOperatorData.idOperator) {
            await this.loadElementoOptions(this.currentOperatorData.idOperator);
            console.log('[ConditionDrawer] Elemento options loaded:', this.elementoOptions.length, 'options');
            console.log('[ConditionDrawer] Elemento options values:', this.elementoOptions.map(o => o.value));

            // Verificar que el valor existe en las opciones
            if (parsedSaveValue !== '' && parsedSaveValue !== null && parsedSaveValue !== undefined) {
              const valueToSet = typeof parsedSaveValue === 'string' ? Number(parsedSaveValue) : parsedSaveValue;
              const existsInOptions = this.elementoOptions.some(opt => opt.value === valueToSet);
              console.log('[ConditionDrawer] Value exists in options?', existsInOptions, 'valueToSet:', valueToSet);
              if (!existsInOptions) {
                console.warn('[ConditionDrawer] Value not found in options!', valueToSet);
              }
            }
          } else {
            this.enableV1Elemento = false;
            this.elementoOptions = [];
          }

          if (this.enableV2 && this.currentOperatorData.idOperator) {
            await this.loadEntryValueOptions(
              this.currentOperatorData.idOperator
            );
          } else {
            this.enableV2Dropdown = false;
            this.entryValueOptions = [];
          }
        }
      }
    }

    // Valor de entrada: multiselect V2 → array en el control; resto → escalar (#544)
    let parsedEntryValue: unknown = conditionValue.entryValue;
    if (this.enableV2 && this.enableV2Dropdown && this.multiSelectV2) {
      parsedEntryValue = this.parseEntryValueForEdit(conditionValue.entryValue);
    }

    const saveValueForControl =
      conditionValue.saveValue === '-1'
        ? ''
        : this.resolveSaveValueForControl(parsedSaveValue);

    console.log('[ConditionDrawer] Setting form values - saveValue:', parsedSaveValue);
    this.conditionForm.patchValue(
      {
        condRule: conditionValue.condRule,
        posC: conditionValue.posC,
        kommentar: conditionValue.kommentar,
        saveValue: saveValueForControl,
        grupNr: conditionValue.grupNr,
        entryValue: parsedEntryValue,
        negCond: normalizeSnFlag(conditionValue.negCond, 'N'),
        exitCond: normalizeSnFlag(conditionValue.exitCond, 'N'),
        visible: normalizeVisibleFlag(conditionValue.visible, 'S'),
        isolate:
          this.labType === 'LABM' ? (conditionValue.isolate || '') : '',
        tipoRes: conditionValue.tipoRes || '',
        heredar: conditionValue.saveValue === '-1',
        condId: conditionValue.condId,
        rOperator:
          operatorKeyResolved ||
          (conditionValue.rOperator !== undefined &&
          conditionValue.rOperator !== null
            ? String(conditionValue.rOperator)
            : ''),
        labelOperator:
          this.currentOperatorData?.labelOperator ??
          conditionValue.labelOperator,
        catalogROperator: (() => {
          const raw =
            conditionValue.catalogROperator ??
            this.currentOperatorData?.catalogROperator;
          if (raw == null || String(raw).trim() === '') {
            return '';
          }
          return String(raw).trim();
        })(),
      },
      { emitEvent: false }
    );

    // Multiselect V2: reaplicar tras render (mismo patrón que saveValue + virtual scroll)
    if (this.enableV2 && this.enableV2Dropdown && this.multiSelectV2) {
      const entryArr = this.parseEntryValueForEdit(conditionValue.entryValue);
      setTimeout(() => {
        this.conditionForm.patchValue(
          { entryValue: entryArr },
          { emitEvent: false }
        );
        this.updateDynamicValidation();
        this._cdr.detectChanges();
      }, 200);
    }

    console.log('[ConditionDrawer] Form saveValue after patchValue:', this.conditionForm.get('saveValue')?.value);
    console.log('[ConditionDrawer] Form saveValue control valid:', this.conditionForm.get('saveValue')?.valid);

    this.updateDynamicValidation();

    // Detectar cambios al final
    this._cdr.detectChanges();
  }

  private resetForm(): void {
    this.conditionForm.reset();
    this.resetDynamicFields();

    // Si estamos en modo nuevo y condition tiene condRule, establecerlo después de reset
    if (this.condition?.condRule && !this.isEditMode && this.visible) {
      // Usar setTimeout para asegurar que el reset se complete antes de establecer el valor
      setTimeout(() => {
        this.conditionForm.patchValue({
          condRule: this.condition!.condRule,
          posC: this.condition!.posC || 0
        });
      }, 0);
    }
  }

  /**
   * Carga los operadores dinámicos disponibles para un condId específico.
   *
   * Este método obtiene la lista de operadores que se pueden usar con el "Actúa Sobre"
   * seleccionado. Cada operador contiene:
   * - posR: ID del operador
   * - labelOperator: Nombre del operador
   * - idOperator: ID usado para ejecutar QRY1 y QRY2
   * - enableV1, enableV2: Flags que determinan qué campos mostrar
   * - multiSelectV1, multiSelectV2: Flags que determinan si usar multiselect
   * - QRY1: Consulta SQL para cargar opciones de "ID Elemento" (se ejecuta en backend)
   * - QRY2: Consulta SQL para cargar opciones de "Valor de Entrada" (se ejecuta en backend)
   *
   * @async
   * @param {string} condId - El ID del "Actúa Sobre" seleccionado
   * @returns {Promise<void>}
   */
  async loadDynamicOperators(condId: string): Promise<void> {
    if (!condId) {
      this.dynamicOperatorOptions = [];
      this.resetDynamicFields();
      return;
    }

    try {
      const result = await this._wxrlService.getOperatorsByCondId(condId);
      this.dynamicOperatorOptions = this.withOperatorKeys(result);
    } catch (error) {
      console.error('Error loading dynamic operators:', error);
      this.dynamicOperatorOptions = [];
      this.resetDynamicFields();
    }
  }

  async onActuaSobreChange(): Promise<void> {
    const condId = this.conditionForm.get('condId')?.value;
    if (condId) {
      await this.loadDynamicOperators(condId);
      this.conditionForm.patchValue({
        rOperator: '',
        labelOperator: '',
        catalogROperator: '',
        saveValue: '',
        entryValue: '',
        heredar: false,
      });
      this.resetDynamicFields(true);
    } else {
      this.resetDynamicFields();
    }
  }

  /**
   * Maneja el cambio de selección en el dropdown "Operador".
   *
   * Cuando el usuario selecciona un operador diferente:
   * 1. Encuentra los datos del operador seleccionado
   * 2. Actualiza los flags dinámicos (enableV1, enableV2, multiSelectV1, multiSelectV2)
   * 3. Carga las opciones para "ID Elemento" si enableV1 es true (ejecuta QRY1)
   * 4. Carga las opciones para "Valor de Entrada" si enableV2 es true (ejecuta QRY2)
   * 5. Resetea `entryValue` (texto vacío o `[]` si combo multiselect V2)
   * 6. Actualiza las validaciones dinámicas
   *
   * @async
   * @returns {Promise<void>}
   */
  async onOperatorChange(): Promise<void> {
    if (this.isLoadingOperatorOptions) {
      return;
    }

    this.closeOperatorInfo();

    const sel = this.conditionForm.get('rOperator')?.value;
    const op = this.findOperatorBySelection(sel);
    if (op && this.dynamicOperatorOptions.length > 0) {
      this.currentOperatorData = op;
      this.enableV1 = this.currentOperatorData.enableV1 === 1;
      this.multiSelectV1 = this.currentOperatorData.multiSelectV1 === 1;
      this.enableV2 = this.currentOperatorData.enableV2 === 1;
      this.multiSelectV2 = this.currentOperatorData.multiSelectV2 === 1;
      this.selectedOperatorInfo = this.readOperatorInfo(
        this.currentOperatorData
      );
      const catOp = this.currentOperatorData.catalogROperator;
      this.conditionForm.patchValue(
        {
          labelOperator: this.currentOperatorData.labelOperator || '',
          catalogROperator:
            catOp != null && String(catOp).trim() !== ''
              ? String(catOp).trim()
              : '',
        },
        { emitEvent: false }
      );

      this.isLoadingOperatorOptions = true;

      try {
        if (this.enableV1 && this.currentOperatorData.idOperator) {
          await this.loadElementoOptions(this.currentOperatorData.idOperator);
        } else {
          this.enableV1Elemento = false;
          this.elementoOptions = [];
        }

        if (this.enableV2 && this.currentOperatorData.idOperator) {
          await this.loadEntryValueOptions(this.currentOperatorData.idOperator);
        } else {
          this.enableV2Dropdown = false;
          this.entryValueOptions = [];
        }

        const emptyEntry =
          this.multiSelectV2 && this.enableV2Dropdown ? [] : '';
        this.conditionForm.patchValue(
          { entryValue: emptyEntry },
          { emitEvent: false }
        );

        this.updateDynamicValidation();
        this._cdr.detectChanges();
      } finally {
        this.isLoadingOperatorOptions = false;
      }
    } else {
      this.resetDynamicFields(true);
    }
  }

  /**
   * Parsea el valor de saveValue para mostrarlo correctamente en el formulario de edición.
   *
   * Lógica de SaveValue y "Heredar" según documentación:
   * - Si SaveValue === '-1' → Checkbox "Heredar" marcado, dropdown "ID Elemento" vacío
   * - Si SaveValue !== '-1' → Checkbox "Heredar" desmarcado, dropdown con el valor seleccionado
   *
   * Cuando se guarda:
   * - Si "Heredar" está marcado → SaveValue = '-1'
   * - Si "Heredar" está desmarcado → SaveValue = valor del dropdown (o valores separados por coma si es multiselect)
   *
   * @private
   * @param {any} saveValue - El valor de saveValue de la condición (puede ser string, number, o array)
   * @returns {any} - El valor parseado para el formulario:
   *   - Si es '-1' o vacío → retorna '' (string vacío)
   *   - Si es multiselect y contiene comas → retorna array de valores
   *   - En otros casos → retorna el valor original
   */
  private parseSaveValueForEdit(saveValue: any): any {
    console.log('[ConditionDrawer] parseSaveValueForEdit - input:', saveValue, 'type:', typeof saveValue, 'multiSelectV1:', this.multiSelectV1);
    if (saveValue === '-1' || !saveValue) {
      console.log('[ConditionDrawer] parseSaveValueForEdit - returning empty string');
      return '';
    }
    if (
      this.multiSelectV1 &&
      typeof saveValue === 'string' &&
      saveValue.includes(',')
    ) {
      const result = saveValue.split(',').filter((v) => v.trim() !== '');
      console.log('[ConditionDrawer] parseSaveValueForEdit - multiselect result:', result);
      return result;
    }
    console.log('[ConditionDrawer] parseSaveValueForEdit - returning original value:', saveValue);
    return saveValue;
  }

  private resolveSaveValueForControl(saveValue: any): any {
    if (Array.isArray(saveValue)) {
      return saveValue.map((v) => this.resolveElementoOptionValue(v));
    }
    return this.resolveElementoOptionValue(saveValue);
  }

  private resolveElementoOptionValue(value: any): any {
    if (value === '' || value === null || value === undefined) {
      return value;
    }
    const strictMatch = this.elementoOptions.find((opt) => opt.value === value);
    if (strictMatch) {
      return strictMatch.value;
    }
    const normalized = String(value).trim();
    const stringMatch = this.elementoOptions.find(
      (opt) => String(opt.value).trim() === normalized
    );
    return stringMatch ? stringMatch.value : value;
  }

  /**
   * Parsea `EntryValue` de BD para el multiselect de «Valor de entrada» (#544).
   * Persistencia: varios códigos separados por coma (misma convención que saveValue V1).
   */
  private parseEntryValueForEdit(entryValue: unknown): unknown[] {
    if (entryValue === undefined || entryValue === null || entryValue === '') {
      return [];
    }
    if (Array.isArray(entryValue)) {
      return [...entryValue];
    }
    if (typeof entryValue === 'string' && entryValue.includes(',')) {
      return entryValue
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
    }
    return [entryValue];
  }

  /**
   * Carga las opciones para el campo "ID Elemento" (saveValue).
   *
   * Este método ejecuta QRY1 en el backend a través del servicio getElementoOptions().
   * QRY1 es una consulta SQL que se obtiene de la estructura del operador y se ejecuta
   * en el backend para obtener la lista de elementos disponibles.
   *
   * Las opciones se mapean a un formato {label, value} para ser usadas en el dropdown/multiselect.
   *
   * @async
   * @param {string} idOperator - El ID del operador que contiene la consulta QRY1
   * @returns {Promise<void>}
   *
   * @example
   * // Si el operador tiene QRY1 = "SELECT id, name FROM elements WHERE type = 'X'"
   * // El backend ejecuta la consulta y devuelve: [{label: "Element 1", value: 100170}, ...]
   * // Este método mapea el resultado y establece enableV1Elemento = true
   */
  async loadElementoOptions(idOperator: string): Promise<void> {
    try {
      const condId = this.conditionForm.get('condId')?.value;
      const result = await this._wxrlService.getElementoOptions(
        idOperator,
        condId,
        this.labNr
      );
      console.log('[ConditionDrawer] loadElementoOptions - raw result:', result);
      if (result && result.length > 0) {
        this.elementoOptions = result.map((item) => ({
          label: item.label,
          value: item.value,
        }));
        this.enableV1Elemento = true;
        console.log('[ConditionDrawer] loadElementoOptions - mapped options:', this.elementoOptions);
        console.log('[ConditionDrawer] loadElementoOptions - option values:', this.elementoOptions.map(o => ({ value: o.value, type: typeof o.value })));
      } else {
        this.elementoOptions = [];
        this.enableV1Elemento = false;
      }
      this._cdr.markForCheck();
    } catch (error) {
      console.error('[ConditionDrawer] Error loading elemento options:', error);
      this.elementoOptions = [];
      this.enableV1Elemento = false;
      this._cdr.markForCheck();
    }
  }

  /**
   * Carga las opciones para el campo "Valor de Entrada" (entryValue).
   *
   * Este método determina si se debe mostrar un dropdown o un textbox basándose en QRY2:
   *
   * **Lógica según documentación:**
   * - Si QRY2 está vacío → El backend devuelve array vacío [] o null
   *   → enableV2Dropdown = false → Se muestra un textbox (campo de texto libre)
   *
   * - Si QRY2 contiene una consulta SQL → El backend ejecuta la consulta y devuelve opciones
   *   → enableV2Dropdown = true → Se muestra un dropdown con las opciones
   *
   * **Flujo:**
   * 1. Se llama a getEntryValueOptions() que ejecuta QRY2 en el backend
   * 2. Si el resultado tiene opciones (array con elementos):
   *    - Se mapean a formato {label, value}
   *    - enableV2Dropdown = true → HTML muestra p-dropdown
   * 3. Si el resultado está vacío o es null (QRY2 vacío):
   *    - enableV2Dropdown = false → HTML muestra input text
   *
   * @async
   * @param {string} idOperator - El ID del operador que contiene la consulta QRY2
   * @returns {Promise<void>}
   *
   * @example
   * // Caso 1: QRY2 tiene consulta SQL
   * // Backend ejecuta: "SELECT id, name FROM values WHERE type = 'Y'"
   * // Devuelve: [{label: "Value 1", value: "V1"}, {label: "Value 2", value: "V2"}]
   * // Resultado: enableV2Dropdown = true → Se muestra dropdown
   *
   * // Caso 2: QRY2 está vacío
   * // Backend devuelve: [] o null
   * // Resultado: enableV2Dropdown = false → Se muestra textbox
   */
  async loadEntryValueOptions(idOperator: string): Promise<void> {
    try {
      const condId = this.conditionForm.get('condId')?.value;
      const result = await this._wxrlService.getEntryValueOptions(
        idOperator,
        condId,
        this.labNr
      );
      console.log('[ConditionDrawer] loadEntryValueOptions - result:', result);

      // Si QRY2 está vacío, el backend devuelve un array vacío o null
      // En ese caso, mostrar textbox en lugar de dropdown
      if (result && Array.isArray(result) && result.length > 0) {
        this.entryValueOptions = result.map((item) => ({
          label: item.label,
          value: item.value,
        }));
        this.enableV2Dropdown = true;
        console.log('[ConditionDrawer] Entry value options loaded, showing dropdown:', this.entryValueOptions.length, 'options');
      } else {
        // QRY2 está vacío o no hay opciones → mostrar textbox
        this.entryValueOptions = [];
        this.enableV2Dropdown = false;
        console.log('[ConditionDrawer] No entry value options (QRY2 empty), showing textbox');
      }
      this._cdr.markForCheck();
    } catch (error) {
      console.error('[ConditionDrawer] Error loading entry value options:', error);
      // En caso de error, también mostrar textbox
      this.entryValueOptions = [];
      this.enableV2Dropdown = false;
      this._cdr.markForCheck();
    }
  }

  onOperatorInfoClick(): void {
    if (!this.selectedOperatorInfo) {
      return;
    }
    if (this.showOperatorInfo) {
      this.closeOperatorInfo();
      return;
    }
    this.showOperatorInfo = true;
    this._cdr.markForCheck();
    if (this.operatorInfoTimeout) {
      clearTimeout(this.operatorInfoTimeout);
    }
    this.operatorInfoTimeout = setTimeout(() => this.closeOperatorInfo(), 3000);
  }

  closeOperatorInfo(): void {
    this.showOperatorInfo = false;
    if (this.operatorInfoTimeout) {
      clearTimeout(this.operatorInfoTimeout);
      this.operatorInfoTimeout = null;
    }
    this._cdr.markForCheck();
  }

  private resetDynamicFields(preserveOperatorOptions: boolean = false): void {
    this.closeOperatorInfo();
    this.enableV1 = false;
    this.enableV1Elemento = false;
    this.multiSelectV1 = false;
    this.enableV2 = false;
    this.enableV2Dropdown = false;
    this.multiSelectV2 = false;
    this.selectedOperatorInfo = '';
    this.currentOperatorData = null;
    this.elementoOptions = [];
    this.entryValueOptions = [];
    if (!preserveOperatorOptions) {
      this.dynamicOperatorOptions = [];
    }
  }

  /**
   * Actualiza las validaciones dinámicas de los campos según el estado actual del formulario.
   *
   * **Validación de SaveValue (ID Elemento):**
   * - Si enableV1Elemento es true Y "Heredar" está desmarcado → SaveValue es requerido
   * - Si "Heredar" está marcado → SaveValue no es requerido (se guarda como '-1')
   * - Si enableV1Elemento es false → SaveValue no es requerido
   *
   * **Validación de EntryValue (Valor de Entrada, #544 §8):**
   * - Si enableV2Dropdown es true → obligatorio (combo simple o multiselect)
   * - Si enableV2Dropdown es false (textbox) → no requerido
   * - Si enableV2 es false → sin validadores (campo no visible)
   *
   * @private
   * @returns {void}
   */
  private updateDynamicValidation(): void {
    const saveValueControl = this.conditionForm.get('saveValue');
    const entryValueControl = this.conditionForm.get('entryValue');

    if (this.enableV1Elemento) {
      const heredar = this.conditionForm.get('heredar')?.value;
      if (!heredar) {
        saveValueControl?.setValidators([Validators.required]);
      } else {
        saveValueControl?.clearValidators();
      }
      saveValueControl?.updateValueAndValidity();
    } else {
      saveValueControl?.clearValidators();
      saveValueControl?.updateValueAndValidity();
    }

    if (this.enableV2Dropdown) {
      entryValueControl?.setValidators([
        (c) => this.validateEntryValueComboRequired(c),
      ]);
    } else {
      entryValueControl?.clearValidators();
    }
    entryValueControl?.updateValueAndValidity();
  }

  private updateSaveValueValidation(): void {
    this.updateDynamicValidation();
  }

  /** RULESCONDITION.Relation char(4); sin UI; en edición se reenvía el valor cargado (#549). */
  private relationForSavePayload(): string | null {
    if (!this.isEditMode || !this.condition) {
      return null;
    }
    const raw = this.condition.relation;
    if (raw === undefined || raw === null) {
      return null;
    }
    return String(raw).substring(0, 4);
  }

  /** RULESCONDITION.DebugCond: sin UI; en edición se reenvía el valor cargado (#550). */
  private debugCondForSavePayload(): number | null {
    if (!this.isEditMode || !this.condition) {
      return null;
    }
    const raw = this.condition.debugCond;
    if (raw === undefined || raw === null) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }

  /**
   * Guarda los datos del formulario y emite el evento 'save' con la condición actualizada.
   *
   * **Lógica de SaveValue y "Heredar":**
   * - Si checkbox "Heredar" está marcado → SaveValue = '-1'
   * - Si checkbox "Heredar" está desmarcado:
   *   - Si es multiselect → SaveValue = valores separados por coma (ej: "100170,100180")
   *   - Si es dropdown simple → SaveValue = valor seleccionado (ej: 100170)
   *
   * **Lógica de EntryValue (#544 §7, #545):**
   * - Si el operador resuelto tiene EnableV2 ≠ 1 (control no visible): se guarda `null`.
   * - Si el control es visible: valor del formulario; multiselect V2 → unir con comas.
   *
   * @returns {void}
   */
  private buildConditionData(): any {
    const formValue = this.conditionForm.value;

    // Lógica de SaveValue según requerimientos
    // Si "Heredar" está marcado, guardar '-1' en SaveValue
    let saveValue = formValue.saveValue;
    if (formValue.heredar) {
      saveValue = '-1';
    } else if (this.multiSelectV1 && Array.isArray(saveValue)) {
      // Si es multiselect, unir valores con comas
      saveValue = saveValue.join(',');
    }

    const selectedOp = this.findOperatorBySelection(formValue.rOperator);
    const entryValueVisible = selectedOp?.enableV2 === 1;

    let entryValue: unknown = formValue.entryValue;

    if (!entryValueVisible) {
      entryValue = null;
    } else if (
      selectedOp?.multiSelectV2 === 1 &&
      Array.isArray(entryValue)
    ) {
      // No exigir this.enableV2Dropdown: el formulario ya refleja multiselect (array).
      entryValue = (entryValue as unknown[]).map((v) => String(v)).join(',');
    }
    let rOperatorOut: number | string = formValue.rOperator;
    if (selectedOp) {
      rOperatorOut = Number(selectedOp.posR);
    } else {
      const n = Number(formValue.rOperator);
      if (!Number.isNaN(n)) {
        rOperatorOut = n;
      }
    }
    const labelOperatorPersist =
      selectedOp?.labelOperator ||
      formValue.labelOperator ||
      this.selectedOperatorInfo;

    const catalogROperatorOut = (() => {
      const fromOp = selectedOp?.catalogROperator;
      if (fromOp != null && String(fromOp).trim() !== '') {
        return String(fromOp).trim();
      }
      const fromForm = formValue.catalogROperator;
      if (fromForm != null && String(fromForm).trim() !== '') {
        return String(fromForm).trim();
      }
      return '';
    })();

    return {
      condRule: formValue.condRule,
      posC: formValue.posC,
      kommentar:
        formValue.kommentar == null ? '' : String(formValue.kommentar).trim(),
      condId: formValue.condId,
      saveValue: saveValue,
      grupNr:
        formValue.grupNr === undefined ||
        formValue.grupNr === null ||
        formValue.grupNr === ''
          ? 0
          : formValue.grupNr,
      rOperator: rOperatorOut,
      labelOperator: labelOperatorPersist,
      catalogROperator: catalogROperatorOut,
      ...(selectedOp?.idOperator != null && selectedOp.idOperator !== ''
        ? { idOperator: selectedOp.idOperator }
        : {}),
      entryValue: entryValue,
      negCond: normalizeSnFlag(formValue.negCond, 'N'),
      exitCond: normalizeSnFlag(formValue.exitCond, 'N'),
      visible: normalizeVisibleFlag(formValue.visible, 'S'),
      isolate:
        this.labType === 'LABM' ? (formValue.isolate || '') : '',
      tipoRes: formValue.tipoRes || '',
      relation: this.relationForSavePayload(),
      debugCond: this.debugCondForSavePayload(),
    };
  }

  onSave(): void {
    if (this.conditionForm.valid) {
      const conditionData = this.buildConditionData();
      this.save.emit(conditionData);
    } else {
      Object.keys(this.conditionForm.controls).forEach((key) => {
        this.conditionForm.get(key)?.markAsTouched();
      });
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visibleChange.emit(value);
  }

  get drawerTitle(): string {
    return this.isEditMode
      ? this._translate.instant('WXRL.SCHEMA.EDIT_CONDITION')
      : this._translate.instant('WXRL.SCHEMA.NEW_CONDITION');
  }

  get showIsolation(): boolean {
    return this.labType === 'LABM';
  }
}
